import { UISref, useRouter } from "@uirouter/react";
import moment from "moment";
import { FunctionComponent, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Option } from "space-monad";
import styled from "styled-components";
import { PrimaryButton } from "../../../../components/button";
import { Icon } from "../../../../components/icons";
import { KycIcon } from "../../../../components/icons/status";
import { ellipsis } from "../../../../components/layout";
import { BooleanField, Table } from "../../../../components/table";
import { TableHeaders } from "../../../../components/table/Head";
import { Pageable } from "../../../../services/pageable";
import { AppStore } from "../../../../store";
import { UserActions } from "../../../../store/users/actions";
import { UsersDTO } from "../../../../store/users/models/dto";
import { colours } from "../../../../styles/colours";
import { LockingTableCTA } from "../../detail/common/components";
import { UsersRoutes as routes } from "../../routes";
import { NoUsers } from "../base";
import { PresentationalUsersProps } from "./common";

type SingleUser = UsersDTO.Responses.GetSingle.T;

type EnhancedSingleUser = UsersDTO.Responses.GetSingle.T & {
  detailsEnabled: boolean;
  detailsEmailVerified: boolean;
  detailsAccountLocked: boolean;
  detailsCredentialsExpired: boolean;
  detailsLoginTries: number;
};

type fieldNames = keyof EnhancedSingleUser;

const headers: TableHeaders<fieldNames> = [
  {
    name: "firstName",
    title: "First Name",
  },
  {
    name: "lastName",
    title: "Last Name",
  },
  {
    name: "emailAddress",
    title: "Email Address",
  },
  {
    name: "createdDate",
    title: "Date Created",
  },
  {
    name: "detailsEnabled",
    title: "Enabled",
    hiddenMobile: true,
    centered: true,
  },
  {
    name: "detailsEmailVerified",
    title: "Email Verified",
    hiddenMobile: true,
    centered: true,
  },
  {
    name: "detailsAccountLocked",
    title: "Account Locked",
    hiddenMobile: true,
    centered: true,
  },
  {
    name: "detailsCredentialsExpired",
    title: "Credentials Expired",
    hiddenMobile: true,
    centered: true,
  },
  { name: "kycStatus", title: "KYC Status" },
  {
    name: "userReferenceId",
    title: "",
    centered: true,
    hiddenMobile: true,
  },
];

const OverflowField = styled.span`
  ${ellipsis};
  max-width: 15em;
  display: inline-block;
`;


export const PresentationalUsersList: FunctionComponent<
  Partial<PresentationalUsersProps>
> = ({ usersList, auth }) => {
  const hiddenFields: (
    | fieldNames
    | "detailsEnabled"
    | "detailsEmailVerified"
    | "detailsAccountLocked"
    | "detailsCredentialsExpired"
    | "detailsLoginTries"
  )[] = useMemo(
    () =>
      auth && auth.accessToken.roles.includes("ROLE_ADMIN")
        ? []
        : ["firstName", "lastName"],
    [auth]
  );
  const newUserListContent: Array<EnhancedSingleUser> = useMemo(
    () =>
      Option(usersList)
        .map((x) => x.content)
        .map((x) =>
          x.map((x) => ({
            ...x,
            detailsEnabled: x.userAccountDetails.enabled,
            detailsEmailVerified: x.userAccountDetails.emailVerified,
            detailsAccountLocked: x.userAccountDetails.accountNonLocked,
            detailsCredentialsExpired:
              x.userAccountDetails.credentialsNonExpired,
            detailsLoginTries: x.userAccountDetails.loginTries,
          }))
        )
        .getOrElse([]),
    [usersList?.content]
  );
  const dispatch = useDispatch();
  const router = useRouter();

  const pageableState = useSelector<
    AppStore,
    AppStore["users"]["allUsers"]["pagination"]
  >((store) => store.users.allUsers.pagination);

  const onPaginationChange = (x: Pageable.T<keyof SingleUser>) =>
    dispatch(UserActions.setPagination(x));

  return (
    <>
      {!usersList || usersList.pageable.totalElements < 1 ? (
        <NoUsers />
      ) : (
        <>
          <Table
            data={newUserListContent}
            head={headers}
            renderFields={{
              userReferenceId: ({ userReferenceId }) => (
                <UISref
                  to={routes.names.single}
                  params={{ id: userReferenceId }}
                >
                  <a>
                    <PrimaryButton variant="contained">
                      <Icon
                        type="export"
                        rotateAmount={270}
                        colour1={colours.white}
                      />
                    </PrimaryButton>
                  </a>
                </UISref>
              ),
              firstName: ({ firstName }) => (
                <OverflowField>{firstName}</OverflowField>
              ),
              lastName: ({ lastName }) => (
                <OverflowField>{lastName}</OverflowField>
              ),
              emailAddress: ({ emailAddress }) => (
                <OverflowField>{emailAddress}</OverflowField>
              ),
              createdDate: ({ createdDate }) => (
                <>{moment.utc(createdDate).local().format("yyy-MM-DD HH:mm")}</>
              ),
              kycStatus: ({ kycStatus }) => KycIcon(kycStatus, "kyc"),
              detailsAccountLocked: ({
                detailsAccountLocked,
                userReferenceId,
              }) =>
                detailsAccountLocked ? (
                  <BooleanField colour1={colours.green} />
                ) : (
                  <LockingTableCTA userId={userReferenceId} type="unlock" />
                ),
            }}
            unsortable={[
              "detailsEnabled",
              "detailsEmailVerified",
              "detailsAccountLocked",
              "detailsCredentialsExpired",
              "detailsLoginTries",
            ]}
            rowOnClick={({ userReferenceId }) => {
              router.stateService.go(routes.names.single, {
                id: userReferenceId,
              });
            }}
            hide={hiddenFields}
            pagination={usersList.pageable}
            onPaginationChange={onPaginationChange}
            pageableState={pageableState}
          />
        </>
      )}
    </>
  );
};
